import {Button, Dialog, Icon, Tooltip} from '@startlibs/components';
import {useFormValue, WithForm, AutoComplete} from '@startlibs/form'
import {getColor, postFetcher} from '@startlibs/utils';
import {useToggle} from '@startlibs/core'
import React, {useEffect, useRef, useState} from 'react';
import _ from 'lodash/fp'
import styled from 'styled-components';

import {DATE_FORMAT} from '../../config/DateFormat'
import {addTagToSuggestions, fetchSuggestions} from '../../utils/tagFetch'
import {strToDate} from '../../utils/utils'
import {AutocompleteBox} from '../../components/AutocompleteBox'
import {formatNoUTCDate} from '../../utils'

const Tag = styled(({title, ...props}) => <Tooltip whenEllipsis content={title}>
      <div  {...props}/>
    </Tooltip>
    )`
  border-radius: 8px;
  background: rgba(185, 200, 210, 0.3);
  border: 1px solid rgba(185, 200, 210, 0.6);
  display: inline-block;
  padding: 0 0.5rem;
  margin-right: 0.25rem;
  font-size: 11px;
  line-height: 14px;
  margin-top: 3px;
  cursor: pointer;
  color: rgba(0,0,0,0.75);
  max-width: 122px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: -5px;
  ${Icon} {
    font-size: 14px;
    line-height: 0;
    vertical-align: -2px;
    color: rgba(0,0,0,0.6);
  }
  :hover, :active {
    background: rgba(185, 200, 210, 0.6);
    border: 1px solid rgba(185, 200, 210, 0.9);
  }
`
const CreateOptionItem = styled.span`
  color: ${getColor('main')};
`

export const getLabel = (v) => v.newValue ? `Create option "${v.newValue}"` : (_.isString(v.description) ? v.description : v)
export const getListItem = (v) => v.newValue ? <CreateOptionItem className="whiteOnHover">Create
  option {v.newValue}</CreateOptionItem> : (_.isString(v.description) ? v.description : v)
const transform = (l) => l ? l.map(v => (v && v.newValue) || v) : l


export const StudyCell = ({study, setParams, setStudy, refreshList}) => {
  const tagDialog = useToggle()

  const filterTag = (tagId) => setParams(_.update('tag', (v = "") => v.split(",").filter(_.identity).filter(id => Number(id) !== Number(tagId)).concat(tagId).join(",")), true)

  return <>
    <span>{study.modalities}</span> - <span>{study.studyDescription}</span>
    <div><span css="margin-right:0.25rem">{study.imageCount} images</span>
      {
        study.tags.length > 0
          ? <>
            <span>{study.tags.map(({description, id_tag}) => <Tag title={description} key={id_tag} onClick={() => filterTag(id_tag)} className="fix-safari-alignment">{description}</Tag>)}</span>
            <Tag onClick={tagDialog.open} className="fix-safari-alignment"><Icon icon="edit"/></Tag>
          </>
          : <Tag onClick={tagDialog.open} className="fix-safari-alignment">+ Tag</Tag>
      }
      {
        tagDialog.isOpen &&
        <TagDialog setStudy={setStudy} study={study} closeDialog={tagDialog.close} refreshList={refreshList}/>
      }
    </div>
  </>
}

const TagDialog = ({setStudy, closeDialog, study, refreshList}) => {

  const autoCompleteRef = useRef()

  const submitTags = ({tags}) => {
    const newTags = _.uniqBy(getLabel, tags).filter(({id_tag}) => !study.tags.find(tag => tag.id_tag === id_tag))
    const removedTags = study.tags.filter(({id_tag}) => !tags.find(tag => tag.id_tag === id_tag))
    return Promise.all(
      newTags
        .map(tag => (
            tag.id_tag
              ? Promise.resolve(tag)
              : postFetcher("/StudyInsertTag.php", {tag_description: tag}).then((id_tag) => {
                const newTag = ({description: tag, id_tag: Number(id_tag)})
                addTagToSuggestions(newTag)
                return newTag
              })
          ).then((tag) =>
            postFetcher("/StudyInsertTagInStudy.php", {id_study: Number(study.id), id_tag: Number(tag.id_tag)})
              .then(() => tag)
          )
        )
      )
      .then(values => console.log(values) ||
        Promise.all(removedTags
          .filter(({id_tag}) => !values.find(tag => Number(tag.id_tag) === Number(id_tag)))
          .map(tag => postFetcher("/StudyRemoveTagInStudy.php", {
            id_study: Number(study.id),
            id_tag: Number(tag.id_tag)
          }).then(() => null))
        ).then(() => values)
      )
      .then((values) => removedTags.length ? refreshList().then(() => []) : values)
  }


  const editTagsSuccess = (t, response) => {
    if (response) {
      setStudy(_.update('study.tags', t => t.concat(response)));
    }
    closeDialog()
  }

  React.useEffect(() => {
    fetchSuggestions()()
  }, [])


  const onAddValue = (tags, setTags) => {
    const current = getLabel(tags.slice(-1)[0])
    const index = tags.slice(0, -1).map(getLabel).map(v => v.toLowerCase()).indexOf(current.toLowerCase())
    if (index >= 0) {
      autoCompleteRef.current.remove(index)
    }
  }


  return <WithForm
    action={submitTags}
    values={{tags: study.tags}}
    onSuccess={editTagsSuccess}
  >{form =>
    <Dialog
      withFormWrap
      onConfirm={form.willSubmitForm}
      closeDialog={closeDialog}
      title="Tag this study"
      footer={<>
        <Button onClick={closeDialog} tabIndex={2}>Cancel</Button>
        <Button highlight type="submit" isLoading={form.isLoading} onClick={form.willSubmitForm}>Update tags</Button>
      </>}
    >
      <p>Add tags to this study.</p>
      <AutoComplete
        autoFocus
        ref={autoCompleteRef}
        onAddValue={onAddValue}
        CompletedBox={AutocompleteBox}
        minQueryLength={1}
        readOnly={form.isLoading}
        label="Tags:"
        confirmIfValueKeys={['Tab']}
        path="tags"
        autoSelectEquals
        fetchSuggestions={fetchSuggestions(true)}
        getLabel={getLabel}
        getListItem={getListItem}
        transform={transform}
        withDropdown
        bellowDescText="Use comma, enter or tab to add multiple tags."
      />
    </Dialog>
  }</WithForm>
}
